<template>
  <div class="ai-markdown" v-html="renderedHtml"></div>
</template>

<script setup>
import { computed } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import hljs from 'highlight.js/lib/core'

// 只注册常用语言，减小包体积
import javascript from 'highlight.js/lib/languages/javascript'
import typescript from 'highlight.js/lib/languages/typescript'
import python from 'highlight.js/lib/languages/python'
import java from 'highlight.js/lib/languages/java'
import sql from 'highlight.js/lib/languages/sql'
import bash from 'highlight.js/lib/languages/bash'
import json from 'highlight.js/lib/languages/json'
import xml from 'highlight.js/lib/languages/xml'
import css from 'highlight.js/lib/languages/css'
import plaintext from 'highlight.js/lib/languages/plaintext'

hljs.registerLanguage('javascript', javascript)
hljs.registerLanguage('js', javascript)
hljs.registerLanguage('typescript', typescript)
hljs.registerLanguage('ts', typescript)
hljs.registerLanguage('python', python)
hljs.registerLanguage('py', python)
hljs.registerLanguage('java', java)
hljs.registerLanguage('sql', sql)
hljs.registerLanguage('bash', bash)
hljs.registerLanguage('shell', bash)
hljs.registerLanguage('json', json)
hljs.registerLanguage('xml', xml)
hljs.registerLanguage('html', xml)
hljs.registerLanguage('css', css)
hljs.registerLanguage('plaintext', plaintext)
hljs.registerLanguage('text', plaintext)

// 配置 marked
const renderer = new marked.Renderer()

// 自定义代码块渲染，支持语法高亮
renderer.code = function ({ text, lang }) {
  const language = lang && hljs.getLanguage(lang) ? lang : 'plaintext'
  const highlighted = hljs.highlight(text, { language }).value
  const langLabel = lang || ''
  return (
    '<div class="ai-code-block">' +
      '<div class="ai-code-header">' +
        '<span class="ai-code-lang">' + langLabel + '</span>' +
        '<span class="ai-code-copy" data-code="' + encodeURIComponent(text) + '">复制</span>' +
      '</div>' +
      '<pre><code class="hljs language-' + language + '">' + highlighted + '</code></pre>' +
    '</div>'
  )
}

// 自定义行内代码
renderer.codespan = function ({ text }) {
  return '<code class="ai-inline-code">' + text + '</code>'
}

// 自定义链接（新窗口打开）
renderer.link = function ({ href, title, text }) {
  const titleAttr = title ? ' title="' + title + '"' : ''
  return '<a href="' + href + '"' + titleAttr + ' target="_blank" rel="noopener noreferrer">' + text + '</a>'
}

// 自定义表格（添加样式类）
renderer.table = function ({ header, body }) {
  return (
    '<div class="ai-table-wrapper">' +
      '<table class="ai-markdown-table">' +
        '<thead><tr>' + header + '</tr></thead>' +
        '<tbody>' + body + '</tbody>' +
      '</table>' +
    '</div>'
  )
}

marked.setOptions({
  renderer,
  breaks: true,
  gfm: true
})

const props = defineProps({
  content: {
    type: String,
    default: ''
  }
})

const renderedHtml = computed(() => {
  if (!props.content) return ''
  const rawHtml = marked.parse(props.content)
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'hr',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
      'strong', 'em', 'del', 'a', 'img', 'table', 'thead', 'tbody',
      'tr', 'th', 'td', 'div', 'span', 'sup', 'sub'
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'class', 'id', 'title', 'alt', 'src',
      'data-code', 'colspan', 'rowspan'
    ],
    ALLOW_DATA_ATTR: false
  })
})
</script>

<script>
export default { name: 'AiMarkdownRenderer' }
</script>

<style lang="scss">
/* Markdown 渲染样式 */
.ai-markdown {
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;

  p {
    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 12px 0 8px;
    font-weight: 600;
    line-height: 1.4;

    &:first-child {
      margin-top: 0;
    }
  }

  h1 { font-size: 18px; }
  h2 { font-size: 16px; }
  h3 { font-size: 15px; }

  ul, ol {
    margin: 4px 0;
    padding-left: 20px;

    li {
      margin: 2px 0;
    }
  }

  blockquote {
    margin: 8px 0;
    padding: 4px 12px;
    border-left: 3px solid var(--el-color-primary);
    background: var(--el-fill-color-lighter);
    border-radius: 0 4px 4px 0;
    color: var(--el-text-color-secondary);
  }

  hr {
    border: none;
    border-top: 1px solid var(--el-border-color-light);
    margin: 12px 0;
  }

  strong {
    font-weight: 600;
  }

  a {
    color: var(--el-color-primary);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

/* 行内代码 */
.ai-inline-code {
  display: inline;
  padding: 1px 5px;
  border-radius: 3px;
  background: var(--el-fill-color);
  color: var(--el-color-danger);
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
}

/* 代码块 */
.ai-code-block {
  margin: 8px 0;
  border-radius: 6px;
  overflow: hidden;
  background: #1e1e2e;
  border: 1px solid rgba(255, 255, 255, 0.06);

  .ai-code-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 12px;
    background: rgba(255, 255, 255, 0.04);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  .ai-code-lang {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
  }

  .ai-code-copy {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: rgba(255, 255, 255, 0.9);
    }
  }

  pre {
    margin: 0;
    padding: 12px;
    overflow-x: auto;

    &::-webkit-scrollbar {
      height: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.15);
      border-radius: 2px;
    }
  }

  code {
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.5;
    color: #cdd6f4;
  }
}

/* Markdown 表格 */
.ai-table-wrapper {
  margin: 8px 0;
  overflow-x: auto;
  border-radius: 6px;
  border: 1px solid var(--el-border-color-light);
}

.ai-markdown-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    padding: 6px 10px;
    border-bottom: 1px solid var(--el-border-color-lighter);
    text-align: left;
    white-space: nowrap;
  }

  th {
    background: var(--el-fill-color-lighter);
    font-weight: 600;
    color: var(--el-text-color-primary);
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    background: var(--el-fill-color-lighter);
  }
}
</style>
